import { LinkButton, OutlineButton } from '@rexlabs/button';
import LoadingSpinner from '@rexlabs/loading-spinner';
import { margin, StyleSheet, useStyles } from '@rexlabs/styling';
import React, { ReactNode } from 'react';
import { Modal, ModalTrigger } from 'src/components/elements/modal';
import {
  LeadDestinationPreview,
  useRexIntegration,
  getDestinationName,
  LeadDestinations
} from 'src/features/rex-integration';
import { useFeature } from 'src/utils/feature-flags';
import { AdditionalLeadEmailModal } from './AdditionalLeadEmailModal';
import { useAddLeadCaptureEmail } from '../api/useAddLeadCaptureEmail';
import { useLeadCaptureEmails } from '../api/useLeadCaptureEmails';
import { useDeleteLeadCaptureEmail } from '../api/useDeleteLeadCaptureEmail';

const styles = StyleSheet({
  container: {
    '& > * + *': {
      ...margin.styles({
        top: 's'
      })
    }
  },

  destinations: {
    color: ({ token }) => token('color.textStyle.body.subtext')
  },

  removeButton: {
    fontSize: ({ token }) => token('typography.size.m'),
    color: ({ token }) => token('color.textStyle.body.default')
  },

  leadCaptureEmail: {
    '& > * + *': {
      ...margin.styles({
        left: 'xxs'
      })
    }
  }
});

export type LeadPreferenceSummaryProps = {
  campaignId: string;
};

export const LeadPreferenceSummary = ({
  campaignId
}: LeadPreferenceSummaryProps) => {
  const s = useStyles(styles);
  const { data: rexIntegration, ...rexIntegrationQuery } =
    useRexIntegration('test');
  const { data: leadCaptureEmails, ...leadCaptureEmailQuery } =
    useLeadCaptureEmails(campaignId);
  const addLeadCaptureEmail = useAddLeadCaptureEmail(campaignId);
  const removeLeadCaptureEmail = useDeleteLeadCaptureEmail(campaignId);
  const hasMultipleEmailsFeature = !!useFeature('multiple_lead_capture_emails');

  if (rexIntegrationQuery.isLoading || leadCaptureEmailQuery.isLoading)
    return <LoadingSpinner />;

  const dests: ReactNode[] = (
    rexIntegration?.attributes.ready && rexIntegration?.attributes.is_rex_client
      ? rexIntegration?.attributes.lead_preferences ?? []
      : (['email_address'] as LeadDestinations[])
  ).map(getDestinationName);

  // Add all lead capture emails to the destinations
  if (leadCaptureEmails && leadCaptureEmails.length > 0) {
    leadCaptureEmails.forEach((leadCaptureEmail) => {
      dests.push(
        <span key={leadCaptureEmail.id} {...s('leadCaptureEmail')}>
          {addLeadCaptureEmail.isLoading && (
            <LoadingSpinner size={15} strokeWidth={3} />
          )}
          <span>{leadCaptureEmail.email}</span>
          <LinkButton
            {...s('removeButton')}
            onClick={() => removeLeadCaptureEmail.mutate(leadCaptureEmail.id)}
            isLoading={removeLeadCaptureEmail.isLoading}
            isDisabled={addLeadCaptureEmail.isLoading}
          >
            Remove
          </LinkButton>
        </span>
      );
    });
  }

  // Check if user can add more emails based on feature flag
  const currentEmailCount = leadCaptureEmails?.length ?? 0;
  const canAddMoreEmails = hasMultipleEmailsFeature || currentEmailCount < 2;

  return (
    <div {...s('container')}>
      <LeadDestinationPreview
        {...s('destinations')}
        destinations={dests.filter(Boolean)}
      />
      {canAddMoreEmails && (
        <Modal>
          <ModalTrigger asChild>
            <OutlineButton>Add additional emails</OutlineButton>
          </ModalTrigger>
          <AdditionalLeadEmailModal
            onSubmit={(values) => addLeadCaptureEmail.mutate(values)}
            hasMultipleEmailsFeature={hasMultipleEmailsFeature}
            currentEmailCount={currentEmailCount}
          />
        </Modal>
      )}
    </div>
  );
};
